import type { ComponentType } from 'react'

import {
  File01Icon,
  GameController01Icon,
  Image01Icon,
  LinkSquare01Icon,
  Mic01Icon,
  StarIcon,
  Target02Icon,
  Medal01Icon,
  UserGroupIcon,
  Video01Icon,
} from '@hugeicons/react'

import type { ModuleNavItem } from '@/modules'
import { createModuleIcon } from '@/shared/ui/ModuleIcon'
import { isEvnttzResolvableSlug, resolveEvnttzIconName } from '@/shared/ui/EvnttzIcons/catalog'
import type { GamificationPageModuleType } from '@/types/gamificationPage'

interface ModuleTypeConfig {
  icon: ComponentType<any>
  href: string
}

export const MODULE_TYPE_NAV_CONFIG: Record<GamificationPageModuleType, ModuleTypeConfig> = {
  journeys:  { icon: createModuleIcon('journeys'),      href: '/jornadas' },
  missions:  { icon: createModuleIcon('missions'),        href: '/missoes' },
  quizzes:   { icon: createModuleIcon('quizzes'),        href: '/quizzes' },
  prizes:    { icon: createModuleIcon('prizes'),       href: '/sorteios' },
  rewards:   { icon: createModuleIcon('shop'),         href: '/lojinha' },
  forum:     { icon: createModuleIcon('social'),         href: '/feed' },
  schedule:  { icon: createModuleIcon('schedule'),       href: '/programacao' },
  partners:  { icon: createModuleIcon('partners'),  href: '/parceiros' },
  library:   { icon: createModuleIcon('library'),      href: '/biblioteca' },
  faq:       { icon: createModuleIcon('support'),      href: '/duvidas-frequentes' },
  matchmaking: { icon: createModuleIcon('handshake'),  href: '/conexoes' }
}

// Backward compat: old HugeIcon slugs stored in DB before sprite migration
const LEGACY_ICON_SLUG_MAP: Record<string, ComponentType<any>> = {
  'target':      Target02Icon,
  'star':        StarIcon,
  'trophy':      Medal01Icon,
  'gamepad':     GameController01Icon,
  'video':       Video01Icon,
  'microphone':  Mic01Icon,
  'image':       Image01Icon,
  'document':    File01Icon,
  'link':        LinkSquare01Icon,
  'users':       UserGroupIcon
}

export function resolveHugeIcon(slug: string | null | undefined): ComponentType<any> {
  if (!slug) return createModuleIcon('modules')
  if (isEvnttzResolvableSlug(slug)) {
    return createModuleIcon(resolveEvnttzIconName(slug))
  }
  return LEGACY_ICON_SLUG_MAP[slug] ?? StarIcon
}

import type { GamificationPage } from '@/types/gamificationPage'

/**
 * A página de `module_type: :home` é reservada: o participante chega nela por
 * `/`, nunca por item de menu. Ela é semeada pelo editor de blocos, então todo
 * evento configurado tem uma.
 */
const NON_NAVIGABLE_MODULE_TYPES = ['home']

/**
 * Uma página só vira item de menu se estiver ativa e — quando for página de
 * módulo — se esta versão do app souber desenhá-la.
 *
 * `hidden` conta: a api define o menu como `active.where(hidden: false)`
 * (`GamificationPage.visible_in_menu`) e o app ignorava a coluna, então o
 * interruptor "Escondido" do produtor não fazia nada para o participante.
 *
 * O último caso não é hipotético: a api ganhou `module_type: :home` e o app
 * quebrava inteiro em `MODULE_TYPE_NAV_CONFIG[page.module_type].icon`, porque a
 * navegação assumia que todo valor do enum tinha entrada aqui. Mesma regra do
 * renderer de blocos: valor desconhecido é ignorado, nunca derruba a tela.
 */
export function isNavigablePage(page: GamificationPage): boolean {
  if (!page.active || page.hidden) return false
  if (!page.module_type) return true
  if (NON_NAVIGABLE_MODULE_TYPES.includes(page.module_type)) return false

  return page.module_type in MODULE_TYPE_NAV_CONFIG
}

/**
 * RF-11: o módulo de matchmaking não aparece sem vocabulário de tags configurado
 * no evento, mesmo com a página criada — o motor não teria o que recomendar.
 * `hasNetworkingVocabulary` vem do snapshot já em cache (sem request extra).
 */
export function isNavigablePageWithVocabulary(
  page: GamificationPage,
  hasNetworkingVocabulary: boolean
): boolean {
  if (!isNavigablePage(page)) return false
  if (page.module_type === 'matchmaking' && !hasNetworkingVocabulary) return false

  return true
}

const LEGACY_FORUM_TITLES = new Set(['fórum', 'forum'])

/** Normaliza títulos legados do módulo `forum` para o nome de produto atual. */
export function resolveModuleDisplayTitle(page: GamificationPage): string {
  if (page.module_type !== 'forum') return page.title ?? ''

  const normalized = page.title?.trim().toLowerCase()
  if (!normalized || LEGACY_FORUM_TITLES.has(normalized)) return 'Feed Social'

  return page.title ?? 'Feed Social'
}

export function toNavItem(page: GamificationPage): ModuleNavItem {
  const config = page.module_type
    ? MODULE_TYPE_NAV_CONFIG[page.module_type]
    : undefined

  if (page.module_type && config) {
    return {
      slug: page.module_type,
      icon: config.icon,
      name: resolveModuleDisplayTitle(page),
      href: config.href
    }
  }

  return {
    slug: `custom-${page.id}`,
    icon: resolveHugeIcon(page.icon_slug),
    name: page.title,
    href: `/p/${page.slug ?? page.id}`
  }
}
